import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import CountryInfoPanel from '../components/CountryInfoPanel';
import { countriesAPI, githubAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  HOME_DESKTOP_MIN_WIDTH_PX,
  HOME_COUNTRIES_SHEET_STRICT_MODE,
} from '../config/homePageCountriesSheetBehavior';
import {
  BrandLabel,
  HomeNavLinks,
  MapControls,
  DesktopCountryList,
  HeroOverlay,
  HomePageLoading,
  HomeWorldMap,
  CountriesBottomSheet,
  InteractionShield,
} from './subComponents/homePage';

function useIsDesktopLayout() {
  const query = `(min-width: ${HOME_DESKTOP_MIN_WIDTH_PX}px)`;
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

const HomePage = () => {
  const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroVisible, setHeroVisible] = useState(true);
  const [availableIsos, setAvailableIsos] = useState([]);
  const [isDashboardTimerActive, setIsDashboardTimerActive] = useState(false);
  const [countriesSheetOpen, setCountriesSheetOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia(`(min-width: ${HOME_DESKTOP_MIN_WIDTH_PX}px)`).matches) return false;
    if (sessionStorage.getItem('vervestacks-explored') !== 'true') return false;
    return HOME_COUNTRIES_SHEET_STRICT_MODE;
  });

  const isDesktop = useIsDesktopLayout();
  const controllerRef = useRef(null);

  const compactExploreMapLocked = !isDesktop && !heroVisible;
  const isInteractionLocked = isDashboardTimerActive;
  const disableMapInteraction = compactExploreMapLocked || isInteractionLocked;

  useEffect(() => {
    if (isDesktop) {
      setCountriesSheetOpen(false);
    } else if (
      HOME_COUNTRIES_SHEET_STRICT_MODE &&
      sessionStorage.getItem('vervestacks-explored') === 'true'
    ) {
      setCountriesSheetOpen(true);
    }
  }, [isDesktop]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [countriesRes, isoRes] = await Promise.all([
          countriesAPI.getAll(),
          githubAPI.getAvailableIsos(),
        ]);

        setCountries(countriesRes.countries);
        setAvailableIsos(isoRes.isos || []);
      } catch (err) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    if (sessionStorage.getItem('vervestacks-explored') === 'true') {
      setHeroVisible(false);
    }
  }, []);

  const handleDashboardTimerActiveChange = useCallback((active) => {
    setIsDashboardTimerActive(active);
  }, []);

  const handleCountrySelect = useCallback(
    (country) => {
      if (isDashboardTimerActive) return;
      setSelectedCountry(country);
    },
    [isDashboardTimerActive]
  );

  const handleCloseInfoPanel = useCallback(() => {
    setSelectedCountry(null);
  }, []);

  const handleExploreClick = useCallback(() => {
    setHeroVisible(false);
    sessionStorage.setItem('vervestacks-explored', 'true');
    if (typeof window !== 'undefined' && !window.matchMedia(`(min-width: ${HOME_DESKTOP_MIN_WIDTH_PX}px)`).matches) {
      setCountriesSheetOpen(true);
    }
  }, []);

  const handleCloseCountriesSheet = useCallback(() => {
    setCountriesSheetOpen(false);
  }, []);

  const handleOpenCountriesSheet = useCallback(() => {
    if (isDashboardTimerActive) return;
    setCountriesSheetOpen(true);
  }, [isDashboardTimerActive]);

  const handleReset = useCallback(() => {
    if (isDashboardTimerActive) return;
    setSelectedCountry(null);
    controllerRef.current?.resetView();
  }, [isDashboardTimerActive]);

  const handleDashboardClick = useCallback(() => {
    if (selectedCountry?.iso_code) {
      navigate(`/country/${selectedCountry.iso_code}/overview`);
    }
  }, [selectedCountry, navigate]);

  if (loading) {
    return <HomePageLoading />;
  }

  return (
    <div className="vs-home-root relative min-h-screen">
      {!heroVisible && <BrandLabel />}
      {!heroVisible && <HomeNavLinks />}

      <div className="vs-home-map-shell relative h-screen">
        <div className="absolute inset-0">
          <HomeWorldMap
            countries={countries}
            selectedCountry={selectedCountry}
            onCountrySelect={handleCountrySelect}
            controllerRef={controllerRef}
            availableIsos={availableIsos}
            disableMapInteraction={disableMapInteraction}
          />
        </div>

        {!heroVisible && (
          <DesktopCountryList
            countries={countries}
            selectedCountry={selectedCountry}
            onCountrySelect={handleCountrySelect}
          />
        )}

        <AnimatePresence>
          {heroVisible && <HeroOverlay onExplore={handleExploreClick} />}
        </AnimatePresence>

        {!heroVisible && (
          <MapControls
            onReset={handleReset}
            onOpenCountries={handleOpenCountriesSheet}
            showCountriesLauncher={!isDesktop && !countriesSheetOpen}
            disabled={isInteractionLocked}
          />
        )}

        <InteractionShield active={isInteractionLocked} />

        {!heroVisible && (
          <CountryInfoPanel
            country={selectedCountry}
            isVisible={!!selectedCountry}
            onClose={handleCloseInfoPanel}
            onDashboardClick={handleDashboardClick}
            onDashboardTimerActiveChange={handleDashboardTimerActiveChange}
          />
        )}
      </div>

      {!heroVisible && !isDesktop && (
        <CountriesBottomSheet
          isOpen={countriesSheetOpen}
          onClose={handleCloseCountriesSheet}
          countries={countries}
          selectedCountry={selectedCountry}
          onCountrySelect={handleCountrySelect}
        />
      )}
    </div>
  );
};

export default React.memo(HomePage);
